import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Tooltip, Typography } from "@mui/material";
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Loading from "../../Components/Loading";
import Mensagem from "../../Components/Mensagem";
import { ButtonBase, TextFieldBase } from "../Pedidos/styles";
import PainelCard from "./PainelCard";
import ModalInconsistencias from "../IntegracaoSisdm/ModalInconsistencias";
import { listarInconsistencias, obterSituacaoPeriodo } from "../IntegracaoSisdm/integracaoApi";

const IntegracaoCard = () => {
    const navigate = useNavigate();
    const nivelUsuario = window.localStorage.getItem('nivelUsuario');

    const [loading, setLoading] = useState(false);
    const [periodo, setPeriodo] = useState("");
    const [situacao, setSituacao] = useState(null);
    const [carregandoSituacao, setCarregandoSituacao] = useState(true);
    const [mensagemErro, setMensagemErro] = useState('');
    const [inconsistencias, setInconsistencias] = useState([]);
    const [showInconsistencias, setShowInconsistencias] = useState(false);

    useEffect(() => {
        const dataAtual = new Date();
        const mesFormatado = String(dataAtual.getMonth() + 1).padStart(2, "0");
        setPeriodo(`${dataAtual.getFullYear()}-${mesFormatado}-01`);
    }, []);

    function handleChangePeriodo(e) {
        setPeriodo(`${e.target.value}-01`);
    }

    function periodoFormatoConsulta() {
        return periodo.slice(0, 7).replace('-', '');
    }

    const carregarSituacao = useCallback(async () => {
        if (!/^\d{6}$/.test(periodoFormatoConsulta())) return;

        setCarregandoSituacao(true);
        try {
            const resultado = await obterSituacaoPeriodo(periodoFormatoConsulta());
            setSituacao(resultado);
        } catch (error) {
            console.log("Erro ao verificar a situação do período: ", error);
            setSituacao(null);
        } finally {
            setCarregandoSituacao(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodo]);

    useEffect(() => {
        carregarSituacao();
    }, [carregarSituacao]);

    function abrirTelaIntegracao() {
        navigate(`/integracaosisdm/${periodoFormatoConsulta()}`);
    }

    async function handleAbrirIntegracao(ev) {
        ev?.preventDefault?.();

        if (situacao?.totalmenteIntegrado) {
            abrirTelaIntegracao();
            return;
        }

        setLoading(true);
        setMensagemErro('');

        try {
            const itens = await listarInconsistencias(periodoFormatoConsulta());
            if (itens.length > 0) {
                setInconsistencias(itens);
                setShowInconsistencias(true);
            } else {
                abrirTelaIntegracao();
            }
        } catch (error) {
            console.log("Erro ao verificar inconsistências da integração: ", error);
            setMensagemErro("Erro ao verificar as inconsistências da integração. Tente novamente.");
        } finally {
            setLoading(false);
        }
    }

    function prosseguirIntegracao() {
        setShowInconsistencias(false);
        abrirTelaIntegracao();
    }

    const consultaSomente = Boolean(situacao?.totalmenteIntegrado);
    const podeAbrir = consultaSomente || Boolean(situacao?.podeGerarIntegracao);

    function motivoBloqueio() {
        if (!situacao || consultaSomente) return null;
        if (!situacao.temFaturamentos) return "Não há faturamentos gerados para este período.";
        if (!situacao.periodoConsolidado) return "O faturamento deste período ainda não foi consolidado.";
        if (!situacao.periodoAnteriorIntegrado) return "O período anterior ainda não foi totalmente integrado ao SISDM.";
        return null;
    }

    const mensagemBloqueio = motivoBloqueio();

    function mensagemPositiva() {
        if (mensagemBloqueio) return null;
        if (consultaSomente) return "Período totalmente integrado ao SISDM.";
        if (situacao?.podeGerarIntegracao) return "Período consolidado — pronto para gerar a integração.";
        return null;
    }

    const mensagemSucesso = mensagemPositiva();

    return (
        <PainelCard
            icon={<SyncOutlinedIcon />}
            iconBg="rgba(0, 150, 136, 0.12)"
            iconColor="#00897b"
            title="Integração SISDM"
            chipLabel="Ordens de Serviço"
            chipBg="rgba(0, 150, 136, 0.12)"
            chipColor="#00897b"
            description="Verifica os faturamentos do período e integra ao SISDM."
        >
            {loading ? (
                <Loading />
            ) : (
                <>
                    {mensagemErro && <Mensagem mensagem={mensagemErro} setMensagem={setMensagemErro} variant="danger" />}

                    <Box>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, fontWeight: 500, color: '#132350' }}>
                            Selecione o período
                            <Tooltip title="Período de referência para verificar e integrar as ordens de serviço no SISDM.">
                                <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
                            </Tooltip>
                        </Typography>
                        <TextFieldBase
                            id="periodo-integracao"
                            type="month"
                            fullWidth
                            value={periodo.slice(0, 7)}
                            onChange={handleChangePeriodo}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: mensagemSucesso
                                ? 'rgba(46, 125, 50, 0.08)'
                                : mensagemBloqueio
                                    ? 'rgba(211, 47, 47, 0.06)'
                                    : 'rgba(237, 108, 2, 0.08)',
                            borderRadius: '12px',
                            p: 2,
                            mt: 3,
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#132350', textAlign: 'left' }}>
                                {mensagemSucesso ? "Tudo certo" : "Atenção"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {mensagemSucesso || mensagemBloqueio || "Apenas faturamentos de períodos já consolidados poderão ser integrados."}
                            </Typography>
                        </Box>
                        {mensagemSucesso
                            ? <CheckCircleOutlineIcon sx={{ fontSize: '1.5rem', color: 'success.main' }} />
                            : <WarningIcon sx={{ fontSize: '1.5rem', color: 'warning.main' }} />}
                    </Box>

                    <Box sx={{ display: 'flex', mt: 3 }}>
                        <ButtonBase
                            onClick={handleAbrirIntegracao}
                            variant="contained"
                            startIcon={consultaSomente ? <VisibilityOutlinedIcon /> : <SyncOutlinedIcon />}
                            disabled={nivelUsuario !== "1" || carregandoSituacao || !podeAbrir}
                        >
                            {consultaSomente ? "Consultar Integração" : "Gerar Integração"}
                        </ButtonBase>
                    </Box>
                </>
            )}

            <ModalInconsistencias
                show={showInconsistencias}
                onHide={() => setShowInconsistencias(false)}
                onContinuar={prosseguirIntegracao}
                periodo={periodoFormatoConsulta()}
                itens={inconsistencias}
            />
        </PainelCard>
    );
};

export default IntegracaoCard;
